import type {
  PipelineStage,
  ClosingStatus,
  LeadPipelineStageCategory,
  ActivityType,
  ReachOutActivityType,
  FollowUpObjective,
  HealthStatus,
} from './types';

export const PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
  1: 'Prospecting',
  2: 'Qualification',
  3: 'Need Analysis',
  4: 'Proposal',
  5: 'Negotiation',
  6: 'Decision Making',
  7: 'Closed',
};

export const CLOSING_STATUS_LABELS: Record<number, string> = {
  0: 'Open',
  1: 'Open',
  2: 'Won',
  3: 'Lost',
  4: 'Disqualified',
};

/** Reverse map: label → numeric value (handles string enum from API responses). */
export function closingStatusToNumber(v: ClosingStatus | null): number {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  const lookup: Record<string, number> = { None: 0, Open: 1, Won: 2, Lost: 3, Disqualified: 4 };
  return lookup[v] ?? 0;
}

export function isClosedCategory(cat: LeadPipelineStageCategory): boolean {
  if (typeof cat === 'number') return cat === 2;
  return String(cat).toLowerCase() === 'closed';
}

export function isOpenCategory(cat: LeadPipelineStageCategory): boolean {
  if (typeof cat === 'number') return cat === 1;
  return String(cat).toLowerCase() === 'open';
}

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  1: 'Phone',
  2: 'Email',
  3: 'Social Media',
  4: 'Meeting',
  5: 'Event',
  6: 'Internal Note',
  7: 'Follow-up Snoozed',
  8: 'Walk-in',
  9: 'Other',
  10: 'Follow-up Completed',
};

export const REACH_OUT_ACTIVITY_TYPE_LABELS: Record<ReachOutActivityType, string> = {
  1: 'Phone',
  2: 'Email',
  3: 'Social Media',
  4: 'Meeting',
  5: 'Event',
  8: 'Walk-in',
  9: 'Other',
};

export const FOLLOW_UP_OBJECTIVE_LABELS: Record<FollowUpObjective, string> = {
  1: 'Qualify',
  2: 'Demo',
  3: 'Proposal',
  4: 'Negotiation',
  5: 'Closing',
  6: 'Re-engage',
  7: 'Other',
};

export const HEALTH_STATUS_LABELS: Record<HealthStatus, string> = {
  0: 'None',
  1: 'New (no contact)',
  2: 'At risk',
  3: 'Cold',
};

/** Map pipeline stage definition codes from the API to legacy numeric PipelineStage values. */
export const PIPELINE_STAGE_CODE_TO_STAGE: Record<string, PipelineStage> = {
  open: 1,
  new_lead: 1,
  prospecting: 1,
  qualified: 2,
  contacted_discovery: 3,
  proposal_quote: 4,
  negotiation_review: 5,
  contract_signed: 6,
  closed_won: 7,
  closed_lost: 7,
  closed_disqualified: 7,
};

export const CLOSING_STATUS_OPTIONS = Object.entries(CLOSING_STATUS_LABELS)
  .filter(([value]) => value !== '1')
  .map(([value, label]) => ({ value: Number(value) as ClosingStatus, label }));

/** BANT score (0–5) display labels for dropdowns and detail view */
export const BANT_SCORE_LABELS: Record<number, string> = {
  0: 'Unknown',
  1: 'Very Low',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Very High',
};

export const BANT_SCORE_OPTIONS = [0, 1, 2, 3, 4, 5].map((value) => ({
  value,
  label: BANT_SCORE_LABELS[value],
}));

/** BANT score descriptions for tooltips (guidance, not enforcement) */
export const BANT_DESCRIPTIONS = {
  budget: [
    'No budget identified or available.',
    'Exploring costs; budget not yet requested.',
    'Budget requested but pending approval.',
    'Budget approved but highly restricted/conditional.',
    'Budget fully approved and allocated for this initiative.',
  ],
  authority: [
    'Lower-level employee (no purchasing power).',
    'Influencer (can recommend, cannot authorize).',
    'Evaluator/Manager (builds the business case).',
    'Champion (has direct access to the decision-maker).',
    'Economic Buyer (the ultimate decision-maker/signer).',
  ],
  need: [
    'Latent pain (they have a problem but don\'t care yet).',
    'Acknowledged pain (they know it\'s an issue, no active search).',
    'Active search (they are actively looking for solutions).',
    'High priority (blocking their daily operations).',
    'Critical (missing this will cost severe revenue/compliance).',
  ],
  timeline: [
    'Someday / No set timeframe.',
    'Next Fiscal Year (12+ months).',
    'Next 6 to 12 months.',
    'This Quarter (3 to 6 months).',
    'Immediate (0 to 3 months).',
  ],
};

/** BANT total score (0–20) tier for display badges */
export function getBantTier(total: number): { label: string; color: 'default' | 'warning' | 'processing' | 'success' } {
  if (total <= 5) return { label: 'Low', color: 'default' };
  if (total <= 10) return { label: 'Medium', color: 'warning' };
  if (total <= 15) return { label: 'High', color: 'processing' };
  return { label: 'Very High', color: 'success' };
}
